import { addToast } from '../toasts/actions';
import { getUserInfoById, postUserFile, updateUserInfoById } from 'services/network/api/payment';
import { AppDispatch, RootState } from 'store/types';
import { InvestorState, ListFiles, Investor } from './types';
import { logError } from 'utils/logs';
import { formatBirthdayForApi } from './formatBirthday';

export function getInvestorInformation() {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch({ type: 'FETCH_INVESTOR_REQUEST' });
        const { iss } = getState().account;
        try {
            const { data } = await getUserInfoById(iss);
            const [year, month, day] = data?.birthday?.split(' ')[0]?.split('-') || ['', '', ''];
            const personalDetails = {
                ...data,
                firstname: data.first_name,
                lastname: data.last_name,
                address: data?.address?.address,
                zipCode: data?.address?.zip_code,
                city: data?.address?.city,
                country: data?.address?.country,
                birthday: { day, month, year },
                gender: data?.gender || 'Monsieur',
                birthPlace: {
                    ...data?.address_birth,
                    city: data?.address_birth?.city,
                    zipCode: data?.address_birth?.zip_code,
                    country: data?.address_birth?.country,
                },
                phone: data?.phone || getState().roles.phone,
                number: data?.phone || getState().roles.phone,
            };
            dispatch({ type: 'FETCH_INVESTOR_SUCCESS', payload: personalDetails });
        } catch (err) {
            logError(err as Error);
            dispatch({ type: 'FETCH_INVESTOR_FAIL', payload: err });
        }
    };
}

export function submitInvestorInformation(callback: () => void) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch({ type: 'SEND_INVESTOR_DATA' });
        const { investor, account } = getState();
        const { iss } = getState().account;
        const formattedInvestor = {
            birthday: formatBirthdayForApi(investor.birthday),
            address_birth: {
                address: investor.birthPlace.address,
                zip_code: investor.birthPlace.zipCode,
                city: investor.birthPlace.city,
                country: investor.birthPlace.country,
            },
            iss,
            last_name: investor.lastname || account.lastname,
            first_name: investor.firstname || account.firstname,
            gender: ((investor.gender !== 'Autre') ? investor.gender : '') || 'Monsieur',
            phone: investor?.phone || getState().roles.phone,
            address: {
                address: investor.address,
                zip_code: investor.zipCode,
                city: investor.city,
                country: investor.country
            }
        };
        try {
            await updateUserInfoById(iss, formattedInvestor);
            dispatch({ type: 'RECEIVE_INVESTOR_SUCCESS' });
            if (callback) callback();
        } catch (err) {
            logError(err as Error);
            dispatch({ type: 'RECEIVE_INVESTOR_FAIL' });
            dispatch(addToast({
                type: "error",
                title: "Vos informations n'ont pas pu être mise à jour",
                description: `Une erreur est survenue lors de l'envoi de vos données : ${err}`
            }))
        }
    }
}

export function updateInvestorInfo(investor: InvestorState) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const { birthday, address, zipCode, city, country, lastname, firstname, phone, birthPlace, gender } = investor;
        const updatedInvestor = {
            birthday: birthday || getState().investor.birthday,
            address: address || getState().investor.address,
            zipCode: zipCode || getState().investor.zipCode,
            city: city || getState().investor.city,
            country: country || getState().investor.country,
            lastname: lastname || getState().investor.lastname,
            firstname: firstname || getState().investor.firstname,
            phone: phone || getState().investor.phone,
            birthPlace: birthPlace || getState().investor.birthPlace,
            gender: ((gender || getState().investor.gender) !== 'Autre') ? (gender || getState().investor.gender) : '',

        };
        const formattedInvestor: Investor = {
            birthday: formatBirthdayForApi(updatedInvestor.birthday),
            iss: getState().account.iss,
            last_name: updatedInvestor.lastname,
            first_name: updatedInvestor.firstname,
            gender: (updatedInvestor.gender !== 'Autre') ? (updatedInvestor.gender) : '',
            address: {
                address: updatedInvestor.address,
                zip_code: updatedInvestor.zipCode,
                city: updatedInvestor.city,
                country: updatedInvestor.country
            },
            phone: updatedInvestor.phone,
            address_birth: {
                zip_code: updatedInvestor.birthPlace.zipCode,
                city: updatedInvestor.birthPlace.city,
                country: updatedInvestor.birthPlace.country,
            },
        };
        try {
            await updateUserInfoById(getState().account.iss, formattedInvestor);
            dispatch({ type: 'UPDATE_INVESTOR_INFO', payload: updatedInvestor });
        } catch (err) {
            logError(err as Error);
            dispatch(addToast({
                type: "error",
                title: "Vos informations n'ont pas pu être mise à jour",
                description: `Une erreur est survenue lors de l'envoi de vos données : ${err}`
            }))
        }
    }
}

export function uploadFile(file: File, name: string) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const fileState = getState().investor.files[name as keyof ListFiles];
        dispatch({ type: 'UPLOAD_INVESTOR_FILE_REQUEST', payload: name });
        try {
            const formData = new FormData();
            formData.append('iss', getState().account.iss.toString());
            formData.append('type', fileState.type);
            formData.append('file', file);
            const { data } = await postUserFile(formData, getState().account.iss);
            dispatch({ type: 'UPLOAD_INVESTOR_FILE_SUCCEEDED', payload: { name, url: data.url } });
        } catch (err) {
            logError(err as Error);
            dispatch({
                type: 'UPLOAD_INVESTOR_FILE_FAILED',
                payload: { name, error: err },
            });
            dispatch(addToast({
                type: 'error',
                title: 'Le fichier n\'a pas pu être envoyé',
                description: `Une erreur est survenue lors de l'envoi du fichier: ${err}.`
            }));
        }
    };
}

export function resetFile(name: string) {
    return (dispatch: AppDispatch) => {
        dispatch({ type: 'RESET_INVESTOR_FILE', payload: name });
    }
}
